/**
 * AvatarCard Component
 * Displays a single agent avatar with preview, metadata, and action buttons.
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Box,
  Switch,
} from '@mui/material';
import { Edit2, Trash2, Star, ImageIcon, Video } from 'lucide-react';
import type { AgentAvatar } from '../types';
import { parseTags, formatFileSize, CATEGORY_COLORS } from '../types';

// ============================================
// Props
// ============================================

interface AvatarCardProps {
  avatar: AgentAvatar;
  onEdit: (avatar: AgentAvatar) => void;
  onToggleStatus: (publicId: string, isActive: boolean) => void;
  onDelete: (publicId: string) => void;
}

// ============================================
// Media Preview Sub-component
// ============================================

const MediaPreview: React.FC<{ avatar: AgentAvatar }> = ({ avatar }) => {
  const [imgError, setImgError] = useState(false);
  const previewUrl = avatar.previewUrl;

  if (!previewUrl || imgError) {
    return (
      <Box
        className="flex h-full w-full items-center justify-center bg-slate-100 dark:bg-slate-700"
        aria-label="No preview"
      >
        {avatar.type === 'video' ? (
          <Video size={40} className="text-slate-400" />
        ) : (
          <ImageIcon size={40} className="text-slate-400" />
        )}
      </Box>
    );
  }

  if (avatar.type === 'video') {
    return (
      <video
        src={previewUrl}
        className="h-full w-full object-cover"
        autoPlay
        muted
        preload="metadata"
        loop
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <img
      src={previewUrl}
      alt={avatar.name}
      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
      onError={() => setImgError(true)}
    />
  );
};

// ============================================
// Main Component
// ============================================

export const AvatarCard: React.FC<AvatarCardProps> = ({
  avatar,
  onEdit,
  onToggleStatus,
  onDelete,
}) => {
  const tags = parseTags(avatar.tags);
  const categoryColor =
    avatar.category ? (CATEGORY_COLORS[avatar.category as keyof typeof CATEGORY_COLORS] ?? '#6B7280') : '#6B7280';

  return (
    <Card
      className="group relative flex flex-col overflow-hidden rounded-xl transition-all duration-200 hover:shadow-lg dark:bg-slate-800 dark:shadow-slate-900"
      sx={{
        border: '1px solid',
        borderColor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
        bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#1E293B' : '#ffffff'),
      }}
    >
      {/* ---- Media area ---- */}
      <CardMedia
        component="div"
        sx={{ height: 180, position: 'relative', overflow: 'hidden' }}
        className="bg-slate-100 dark:bg-slate-700"
      >
        <MediaPreview avatar={avatar} />

        {/* Overlay badges (top-left) */}
        <Box className="absolute left-2 top-2 flex flex-col gap-1">
          {/* Type badge */}
          <Chip
            label={avatar.type === 'video' ? 'Video' : 'Image'}
            size="small"
            icon={
              avatar.type === 'video' ? (
                <Video size={12} />
              ) : (
                <ImageIcon size={12} />
              )
            }
            sx={{
              bgcolor: 'rgba(0,0,0,0.55)',
              color: 'white',
              fontSize: '0.65rem',
              height: 20,
              '& .MuiChip-icon': { color: 'white', width: 12, height: 12 },
            }}
          />
          {/* Category badge */}
          {avatar.category && (
            <Chip
              label={avatar.category}
              size="small"
              sx={{
                bgcolor: categoryColor,
                color: 'white',
                fontSize: '0.65rem',
                height: 20,
                textTransform: 'capitalize',
              }}
            />
          )}
        </Box>

        {/* Default star (top-right) */}
        {avatar.isDefault && (
          <Box className="absolute right-2 top-2">
            <Tooltip title="Default avatar">
              <Box
                className="flex h-6 w-6 items-center justify-center rounded-full"
                sx={{ bgcolor: '#F59E0B' }}
              >
                <Star size={12} fill="white" color="white" />
              </Box>
            </Tooltip>
          </Box>
        )}

        {/* Inactive overlay */}
        {!avatar.isActive && (
          <Box
            className="absolute inset-0 flex items-center justify-center"
            sx={{ bgcolor: 'rgba(0,0,0,0.5)' }}
          >
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
              INACTIVE
            </Typography>
          </Box>
        )}
      </CardMedia>

      {/* ---- Content ---- */}
      <CardContent sx={{ p: 2, pb: '12px !important', flexGrow: 1 }}>
        {/* Name */}
        <Typography
          variant="subtitle2"
          className="mb-1 font-semibold text-gray-900 dark:text-slate-100"
          noWrap
          title={avatar.name}
        >
          {avatar.name}
        </Typography>

        {/* File info */}
        <Typography variant="caption" className="block text-gray-400 dark:text-slate-500">
          {avatar.file.fileName} · {formatFileSize(avatar.file.fileSize)}
        </Typography>

        {/* Tags */}
        {tags.length > 0 && (
          <Box className="mt-2 flex flex-wrap gap-1">
            {tags.slice(0, 4).map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#F1F5F9',
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? '#94A3B8' : '#475569',
                }}
              />
            ))}
            {tags.length > 4 && (
              <Typography variant="caption" className="text-gray-400 dark:text-slate-500">
                +{tags.length - 4}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>

      {/* ---- Actions footer ---- */}
      <Box
        className="flex items-center justify-between border-t px-2 py-1"
        sx={{
          borderColor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
        }}
      >
        {/* Active toggle */}
        <Tooltip title={avatar.isActive ? 'Deactivate' : 'Activate'}>
          <Switch
            size="small"
            checked={avatar.isActive}
            onChange={(e) => onToggleStatus(avatar.publicId, e.target.checked)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#3B82F6' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#3B82F6' },
            }}
          />
        </Tooltip>

        <Box className="flex gap-0.5">
          {/* Edit */}
          <Tooltip title="Edit metadata">
            <IconButton
              size="small"
              onClick={() => onEdit(avatar)}
              sx={{
                color: (theme) => (theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280'),
                '&:hover': { color: '#3B82F6', bgcolor: 'rgba(59,130,246,0.1)' },
              }}
            >
              <Edit2 size={15} />
            </IconButton>
          </Tooltip>

          {/* Delete */}
          <Tooltip title="Delete (soft)">
            <IconButton
              size="small"
              onClick={() => onDelete(avatar.publicId)}
              sx={{
                color: (theme) => (theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280'),
                '&:hover': { color: '#EF4444', bgcolor: 'rgba(239,68,68,0.1)' },
              }}
            >
              <Trash2 size={15} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Card>
  );
};
